import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import styled from 'styled-components';
import { FiMinus, FiPlus, FiTarget } from 'react-icons/fi';
import 'leaflet/dist/leaflet.css';
import GoogleMap from './GoogleMap';
import { googleMapsConfigured } from '../../services/googleMaps';

const CUYO_CENTER = [-32.95, -67.9];

const shortPrice = (property) => {
  const currency = property.currency === 'ARS' ? '$' : 'US$';
  const price = Number(property.price) || 0;
  if (price >= 1000000) return `${currency} ${(price / 1000000).toFixed(price % 1000000 ? 1 : 0)}M`;
  return `${currency} ${price >= 1000 ? `${Math.round(price / 1000)}k` : price}`;
};

const priceIcon = (property, active) => L.divIcon({
  className: 'nido-map-marker-host',
  html: `<div class="nido-map-marker${active ? ' is-active' : ''}"><span>${shortPrice(property)}</span><i></i></div>`,
  iconSize: active ? [124, 58] : [98, 50],
  iconAnchor: active ? [62, 54] : [49, 46],
});

const frameMap = (map, points, animate = true) => {
  if (!points.length) map.setView(CUYO_CENTER, 6, { animate });
  else if (points.length === 1) map.setView(points[0], 14, { animate });
  else map.fitBounds(points, { padding: [64, 64], maxZoom: 14, animate });
};

function FitProperties({ points }) {
  const map = useMap();
  useEffect(() => {
    window.setTimeout(() => map.invalidateSize(), 0);
    frameMap(map, points);
  }, [map, points]);
  return null;
}

function NidoMapControls({ points }) {
  const map = useMap();
  return (
    <MapControls aria-label="Controles del mapa">
      <ControlButton type="button" onClick={() => map.zoomIn()} aria-label="Acercar mapa"><FiPlus /></ControlButton>
      <ControlButton type="button" onClick={() => map.zoomOut()} aria-label="Alejar mapa"><FiMinus /></ControlButton>
      <ControlButton type="button" onClick={() => frameMap(map, points)} aria-label="Volver a encuadrar propiedades"><FiTarget /></ControlButton>
    </MapControls>
  );
}

export default function PropertyMap({ properties = [], highlightedPropertyId = null, onPropertyHover }) {
  const navigate = useNavigate();
  const mappedProperties = useMemo(() => properties.filter((property) => {
    const lat = Number(property.location?.lat);
    const lng = Number(property.location?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng);
  }), [properties]);
  const points = useMemo(() => mappedProperties.map((property) => [Number(property.location.lat), Number(property.location.lng)]), [mappedProperties]);
  const openProperty = useCallback((property) => navigate(`/properties/${property._id}`), [navigate]);

  if (googleMapsConfigured) {
    return (
      <MapFrame>
        <GoogleMap properties={mappedProperties} highlightedPropertyId={highlightedPropertyId} onPropertyClick={openProperty} onPropertyHover={onPropertyHover} />
        <MapStatus><StatusDot /><span>{mappedProperties.length} {mappedProperties.length === 1 ? 'ubicación' : 'ubicaciones'}</span></MapStatus>
        <MapSignature><span>NIDO</span> · GOOGLE MAPS</MapSignature>
        {!mappedProperties.length && <EmptyMap><strong>Sin ubicaciones para mostrar</strong><span>Las propiedades aparecerán acá cuando tengan coordenadas.</span></EmptyMap>}
      </MapFrame>
    );
  }

  return (
    <MapFrame>
      <MapContainer center={CUYO_CENTER} zoom={6} scrollWheelZoom zoomControl={false} attributionControl>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors &copy; CARTO'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={20}
        />
        <FitProperties points={points} />
        <NidoMapControls points={points} />
        {mappedProperties.map((property) => {
          const active = String(property._id) === String(highlightedPropertyId);
          return <Marker
            key={property._id}
            position={[Number(property.location.lat), Number(property.location.lng)]}
            icon={priceIcon(property, active)}
            zIndexOffset={active ? 1000 : 0}
            title={property.title || 'Ver propiedad'}
            eventHandlers={{
              click: () => navigate(`/properties/${property._id}`),
              mouseover: () => onPropertyHover?.(property._id),
              mouseout: () => onPropertyHover?.(null),
            }}
          />;
        })}
      </MapContainer>
      <MapStatus>
        <StatusDot />
        <span>{mappedProperties.length} {mappedProperties.length === 1 ? 'ubicación' : 'ubicaciones'}</span>
      </MapStatus>
      <MapSignature><span>NIDO</span> · MAPA DE PROPIEDADES</MapSignature>
      {!mappedProperties.length && <EmptyMap><strong>Sin ubicaciones para mostrar</strong><span>Las propiedades aparecerán acá cuando tengan coordenadas.</span></EmptyMap>}
    </MapFrame>
  );
}

const MapFrame = styled.div`
  position:relative;width:100%;height:100%;min-height:360px;overflow:hidden;background:#e8ebe6;
  isolation:isolate;
  &::after{content:'';position:absolute;inset:0;z-index:410;pointer-events:none;box-shadow:inset 0 0 0 1px rgba(39,63,68,.08),inset 0 -90px 80px -95px rgba(29,48,52,.42);}
  .leaflet-container{width:100%;height:100%;min-height:360px;background:#e8ebe6;font-family:'Space Grotesk',system-ui,sans-serif;}
  .leaflet-tile-pane{filter:saturate(.68) contrast(.96) sepia(.04);}
  .leaflet-control-attribution{margin:0 10px 7px 0!important;padding:3px 7px!important;color:#778487!important;background:rgba(251,250,246,.78)!important;border-radius:7px;font-size:12px;backdrop-filter:blur(8px);}
  .leaflet-control-attribution a{color:#526b70!important;}
  .nido-map-marker-host{background:transparent;border:0;}
  .nido-map-marker{position:relative;display:grid;width:92px;height:40px;place-items:center;color:#fff;background:#29474f;border:1px solid rgba(255,255,255,.9);border-radius:7px 7px 7px 2px;box-shadow:0 8px 24px rgba(32,55,60,.22);font-size:12px;font-weight:720;letter-spacing:-.01em;white-space:nowrap;transform-origin:center bottom;transition:transform .22s ease,background .22s ease,box-shadow .22s ease;}
  .nido-map-marker span{position:relative;z-index:1;}
  .nido-map-marker i{position:absolute;bottom:-6px;width:11px;height:11px;background:inherit;border-right:1px solid rgba(255,255,255,.9);border-bottom:1px solid rgba(255,255,255,.9);transform:rotate(45deg);}
  .nido-map-marker.is-active{width:118px;height:46px;background:#c96f4c;border-radius:9px 9px 9px 3px;box-shadow:0 14px 34px rgba(164,81,52,.35);font-size:13px;transform:translateY(-5px);}
  @media(max-width:780px){.leaflet-control-attribution{display:none;}}
`;
const MapControls = styled.div`
  position:absolute;top:18px;right:18px;z-index:600;display:grid;overflow:hidden;padding:4px;background:rgba(251,250,246,.9);border:1px solid rgba(39,63,68,.12);border-radius:14px;box-shadow:0 12px 30px rgba(34,55,60,.12);backdrop-filter:blur(12px);
`;
const ControlButton = styled.button`
  display:grid;width:38px;height:38px;place-items:center;color:#29474f;background:transparent;border:0;border-bottom:1px solid rgba(39,63,68,.08);font-size:17px;cursor:pointer;transition:.18s ease;
  &:last-child{border-bottom:0;} &:hover{color:#fff;background:#29474f;border-radius:9px;} &:focus-visible{outline:2px solid #c96f4c;outline-offset:-2px;border-radius:9px;}
`;
const MapStatus = styled.div`
  position:absolute;top:18px;left:18px;z-index:500;display:flex;align-items:center;gap:8px;min-height:36px;padding:0 13px;color:#29474f;background:rgba(251,250,246,.9);border:1px solid rgba(39,63,68,.1);border-radius:999px;box-shadow:0 8px 24px rgba(34,55,60,.08);font-size:12px;font-weight:680;backdrop-filter:blur(12px);pointer-events:none;
`;
const StatusDot = styled.i`width:7px;height:7px;background:#c96f4c;border-radius:50%;box-shadow:0 0 0 4px rgba(201,111,76,.12);`;
const MapSignature = styled.div`
  position:absolute;left:18px;bottom:16px;z-index:500;color:#6b7b7e;font-size:12px;font-weight:650;letter-spacing:.15em;pointer-events:none;span{color:#29474f;font-weight:820;}
  @media(max-width:780px){bottom:13px;}
`;
const EmptyMap = styled.div`position:absolute;inset:0;z-index:500;display:grid;place-content:center;justify-items:center;gap:6px;color:#718083;background:rgba(241,240,234,.88);text-align:center;pointer-events:none;strong{color:#29474f;font-size:17px;font-weight:620;}span{max-width:260px;font-size:12px;line-height:1.5;}`;
